import type { PushSubscriptionRequest } from '@lbc/shared';

export interface PushSubscriptionApi {
  getPublicKey: () => Promise<{ publicKey: string }>;
  save: (subscription: PushSubscriptionRequest) => Promise<void>;
}

export type PushSubscriptionRemover = (endpoint: string) => Promise<void>;

const unsupportedMessage = 'Dieser Browser unterstützt keine Web-Push-Benachrichtigungen.';
const permissionDeniedMessage = 'Benachrichtigungen wurden im Browser nicht erlaubt.';
const incompleteMessage = 'Die Browser-Subscription ist unvollständig.';

export function isPushSupported(): boolean {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

/** Enables push, preferring an existing browser subscription, and persists it. */
export async function enablePushSubscription(api: PushSubscriptionApi): Promise<void> {
  assertPushSupport();
  await ensurePermission();

  const { publicKey } = await api.getPublicKey();
  const registration = await navigator.serviceWorker.ready;
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: decodeBase64Url(publicKey),
    }));

  const serialized = serializeSubscription(subscription);
  if (serialized === null) {
    throw new Error(incompleteMessage);
  }
  await api.save(serialized);
}

/** Re-registers an already granted browser subscription without requesting permission. */
export async function synchronizeExistingPushSubscription(
  save: PushSubscriptionApi['save'],
): Promise<boolean> {
  if (!isPushSupported() || Notification.permission !== 'granted') {
    return false;
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription === null) return false;

  const serialized = serializeSubscription(subscription);
  if (serialized === null) return false;
  await save(serialized);
  return true;
}

/** Removes the server record before unsubscribing the browser subscription. */
export async function disablePushSubscription(remove: PushSubscriptionRemover): Promise<void> {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription === null) return;

  await remove(subscription.endpoint);
  await subscription.unsubscribe();
}

function assertPushSupport(): void {
  if (!isPushSupported()) {
    throw new Error(unsupportedMessage);
  }
}

async function ensurePermission(): Promise<void> {
  const permission =
    Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(permissionDeniedMessage);
  }
}

function serializeSubscription(subscription: PushSubscription): PushSubscriptionRequest | null {
  const json = subscription.toJSON();
  if (
    json.endpoint === undefined ||
    json.keys?.p256dh === undefined ||
    json.keys.auth === undefined
  ) {
    return null;
  }
  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  };
}

function decodeBase64Url(value: string): ArrayBuffer {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  const binary = atob(padded);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0)).buffer;
}
